import { useEffect, useState } from "react";
import api from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { Gift, Copy, Check, Users } from "lucide-react";
import { toast } from "sonner";

export default function ReferralCard() {
  const { user } = useAuth();
  const [data, setData] = useState(null);
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    api.get("/me/referrals").then(({ data }) => setData(data)).catch(() => {});
  }, []);

  if (!user) return null;
  const code = data?.referral_code || user.referral_code || "";
  const shareLink = code ? `${window.location.origin}/signup?ref=${code}` : "";

  const copy = async (text, label) => {
    try {
      await navigator.clipboard.writeText(text);
      setCopied(true);
      toast.success(`${label} copied`);
      setTimeout(() => setCopied(false), 1800);
    } catch {
      toast.error("Could not copy. Copy it manually.");
    }
  };

  return (
    <div
      data-testid="referral-card"
      className="min-w-0 w-full overflow-hidden rounded-2xl border border-[#D4AF37]/30 bg-gradient-to-br from-[#0B2115] via-[#0A130E] to-[#030705] p-5 sm:p-6"
    >
      <div className="mb-5 flex min-w-0 items-center justify-between gap-3">
        <div className="flex min-w-0 items-center gap-2 text-xs uppercase tracking-[0.18em] text-[#D4AF37] sm:tracking-[0.2em]">
          <Gift className="h-3.5 w-3.5 shrink-0" />
          <span className="truncate">Refer & earn</span>
        </div>
        <div className="flex shrink-0 items-center gap-1.5 text-xs text-white/60">
          <Users className="h-3.5 w-3.5" />
          <span data-testid="referral-count">{data?.count ?? 0}</span>
          <span className="hidden text-white/40 sm:inline">referred</span>
        </div>
      </div>

      <p className="mb-4 text-sm leading-relaxed text-white/60">
        Share your code with friends. You earn a cash wallet bonus after their first qualifying wallet-to-game load.
      </p>

      <div className="mb-3 flex min-w-0 items-center justify-between gap-3 rounded-xl border border-[#D4AF37]/40 bg-black/40 px-4 py-3">
        <div className="min-w-0">
          <div className="mb-0.5 text-[10px] uppercase tracking-[0.2em] text-white/40">Your code</div>
          <div
            data-testid="referral-code-value"
            className="break-all font-display text-2xl font-bold tracking-[0.2em] gold-text sm:tracking-[0.25em]"
          >
            {code || "-"}
          </div>
        </div>
        <button
          onClick={() => code && copy(code, "Code")}
          disabled={!code}
          data-testid="copy-referral-code"
          className="shrink-0 rounded-lg border border-white/10 bg-white/5 p-2.5 hover:bg-white/10 disabled:opacity-40"
          title="Copy code"
        >
          {copied ? <Check className="h-4 w-4 text-[#D4AF37]" /> : <Copy className="h-4 w-4" />}
        </button>
      </div>

      <button
        onClick={() => shareLink && copy(shareLink, "Link")}
        disabled={!shareLink}
        data-testid="copy-referral-link"
        className="flex w-full min-w-0 items-center justify-between gap-3 rounded-lg border border-white/5 bg-white/[0.02] px-3 py-2.5 text-left text-xs text-white/50 hover:bg-white/5 disabled:opacity-40"
      >
        <span className="min-w-0 flex-1 truncate font-mono">{shareLink || "-"}</span>
        <span className="shrink-0 rounded-md bg-[#D4AF37]/10 px-2 py-1 text-[#D4AF37]">Copy</span>
      </button>
    </div>
  );
}
